import {
  Button,
  CheckboxField,
  ChevronDownIcon,
  Flex,
  Link,
  Loading,
  Popover,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import type { RowData } from '@tanstack/react-table';
import type { LegacyColumn } from '@tanstack/react-table/legacy';

import { ActiveFilterTag } from '../activeFilterTag/activeFilterTag';
import {
  StyledFilterList,
  StyledFilterListButton,
  StyledFilterListCheckboxField,
  StyledFilterListItem,
} from '../filter.styles';

export const ProductSelectFilter = <R extends RowData>({
  column,
  filterOptions,
  isLoading,
}: {
  column: LegacyColumn<R>;
  filterOptions?: string[];
  isLoading?: boolean;
}) => {
  const activeFilters = (column.getFilterValue() ?? []) as string[];
  const filterLabel =
    column.columnDef.meta?.filterLabel ?? sentenceCase(column.id);

  /**
   * Columns whose options aren't plain API values supply their own labels;
   * the default just turns underscores into spaces.
   */
  const optionLabel =
    column.columnDef.meta?.filterOptionLabel ??
    ((option: string) => sentenceCase(option.replace(/_/g, ' ')));
  const noFilterOptions = !filterOptions?.length && !isLoading;

  const onFilterChange = (option: string) => {
    let newFilterValues: string[];

    if (activeFilters.includes(option)) {
      newFilterValues = activeFilters.filter((filter) => filter !== option);
    } else {
      newFilterValues = [...activeFilters, option];
    }
    column.setFilterValue(newFilterValues);
  };

  if (!filterOptions) {
    return null;
  }

  return (
    <Popover
      label={`Filters for ${filterLabel}`}
      target={
        <Button
          label={filterLabel}
          variant={activeFilters.length ? 'tertiary-selected' : 'tertiary'}
          endIcon={
            <Flex alignItems="center">
              <ActiveFilterTag
                valuesCount={activeFilters.length}
                label={filterLabel}
              />
              <ChevronDownIcon color="inherit" size="sm" />
            </Flex>
          }
          disabled={noFilterOptions}
        />
      }
      openOn={noFilterOptions ? 'disabled' : 'hover'}
    >
      {({ closePopover }) => (
        <Popover.Content padding={false}>
          {!filterOptions.length && isLoading ? (
            <Flex
              position="relative"
              height="100%"
              p={24}
              aria-label={`Loading ${filterLabel} filter options`}
            >
              <Loading color="purple" />
            </Flex>
          ) : (
            <StyledFilterList aria-label={`${filterLabel} filter options`}>
              {filterOptions.map((option, index) => (
                <StyledFilterListItem key={index}>
                  <StyledFilterListCheckboxField>
                    <CheckboxField
                      label={optionLabel(option)}
                      name={`${option}-checkbox`}
                      onClick={() => onFilterChange(option)}
                      checked={activeFilters.includes(option)}
                    />
                  </StyledFilterListCheckboxField>
                </StyledFilterListItem>
              ))}
              <StyledFilterListItem>
                <StyledFilterListButton
                  onClick={() => {
                    column.setFilterValue([]);
                    closePopover();
                  }}
                >
                  <Link typography="body">Deselect all</Link>
                </StyledFilterListButton>
              </StyledFilterListItem>
            </StyledFilterList>
          )}
        </Popover.Content>
      )}
    </Popover>
  );
};
